import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { UserManagementProvider } from "@hoteltradergit/user-management";

import { axiosInstance } from "./api";
import { useAuth } from "@hoteltradergit/auth";
import { usePermissions } from "./contexts/PermissionContext";
import { usePropertyStore } from "./contexts/PropertyStoreContext";
import useUmProperties from "./hooks/useUmProperties";
import { getLoggedInUserId, getLoggedInUserType } from "./constants/utils";

// getLoggedInUserId yields null on a missing/malformed token and NaN on a
// non-numeric claim; the package contracts number | null, so coerce NaN.
const safeGetCurrentUserId = () => {
  const id = getLoggedInUserId();
  return Number.isFinite(id) ? id : null;
};

/**
 * Coerce a property id to the `number | null` the package contracts for.
 *
 * A bare `Number()` fails in both directions here. A non-numeric id yields NaN,
 * which is neither null nor usable: the package would read "a hotel is
 * selected" and scope Manage Users to one that can never match, showing an
 * empty list rather than failing. And `Number(null)` is 0 — a *finite* number —
 * so guarding on Number.isFinite alone would turn "no selection" into property
 * 0. Both cases have to collapse to null.
 *
 * API property ids are numeric today, so this is a guard against a future
 * change rather than a live bug.
 */
const toPackagePropertyId = (value) => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Maps the supplier portal's `usePermissions().userInfo` to the package's
 * `CurrentUser` shape so the package's <UserMenu> (header avatar) shows the
 * logged-in user's name, email and avatar.
 *
 * `userInfo.imageUrl` is refreshed by PermissionContext on the
 * `profileImageUpdated` event (which the package's My Profile page dispatches
 * after an upload), so the header avatar updates after a profile image change —
 * matching the GC UI behaviour.
 *
 * Passed to the config as a hook reference; the package invokes it from
 * component render bodies (same pattern as `useProperties`).
 */
export const useSupplierCurrentUser = () => {
  const { userInfo } = usePermissions();
  return useMemo(
    () => ({
      user: {
        firstName: userInfo?.firstName || null,
        lastName: userInfo?.lastName || null,
        email: userInfo?.email || null,
        avatarUrl: userInfo?.imageUrl || null,
      },
      loading: false,
    }),
    [
      userInfo?.firstName,
      userInfo?.lastName,
      userInfo?.email,
      userInfo?.imageUrl,
    ]
  );
};

/**
 * Feeds the top-nav hotel selection to the package, which scopes Manage Users to it — the
 * list shows only users assigned to the selected property and re-fetches on every switch.
 *
 * `loading` covers the window before the property store resolves its initial selection:
 * `propertyId` is null then too, and without this the package would read that as "no hotel
 * selected" and show its error state on every page load.
 *
 * Only the list is scoped. Add User is untouched — creating a user never pre-fills or
 * auto-assigns the top-nav property.
 */
export const useSupplierSelectedProperty = () => {
  const { selectedProperty, selectionResolved } = usePropertyStore();
  const propertyId = selectedProperty?.propertyId ?? null;
  return useMemo(
    () => ({
      propertyId: toPackagePropertyId(propertyId),
      loading: !selectionResolved,
    }),
    [propertyId, selectionResolved]
  );
};

/**
 * Hook that builds a `UserManagementConfig` from the supplier portal's existing
 * contexts (auth, permissions, property store). Used by `<UserManagementBridge>`.
 */
const useSupplierUserManagementConfig = () => {
  const { rolePermissions } = usePermissions();
  const { logOut } = useAuth();

  return useMemo(
    () => ({
      appId: import.meta.env.VITE_PUBLIC_APP_ID || "supplier_portal",
      apiClient: axiosInstance,
      getCurrentUserId: safeGetCurrentUserId,
      // Logged-in user's userType (custom:userType JWT claim). The package hides the
      // "What do I get access to?" link for an internal HotelTrader user.
      getCurrentUserType: getLoggedInUserType,
      onLogout: logOut,
      permissions: { rolePermissions: rolePermissions ?? [] },
      // Hook references — package invokes from component render bodies.
      // Server-side property search: the picker searches the whole catalog over the API rather
      // than filtering the property store's first page of 20.
      useProperties: useUmProperties,
      useCurrentUser: useSupplierCurrentUser,
      // Scopes the Manage Users list to the top-nav hotel (EO-1115).
      useSelectedProperty: useSupplierSelectedProperty,
      // `departments` intentionally omitted — the package ships the canonical per-userType
      // table, and supplier_portal only ever creates SUPPLIER users, so it gets the Supplier
      // column (Finance / Revenue / Sales / Front Desk / Marketing / Development / Others).
    }),
    [rolePermissions, logOut]
  );
};

/**
 * Mounts `<UserManagementProvider>` with the supplier-specific config.
 * Must be rendered inside `<PermissionProvider>` and `<PropertyStoreProvider>`,
 * and inside the `<Router>` (the package's pages depend on react-router context).
 */
export const UserManagementBridge = ({ children }) => {
  const config = useSupplierUserManagementConfig();
  return (
    <UserManagementProvider config={config}>{children}</UserManagementProvider>
  );
};

UserManagementBridge.propTypes = {
  children: PropTypes.node,
};
